import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import Layout from "../../shared/components/Layout";
import SidebarNavigation from '@/components/SidebarNavigation';
import { InputFieldBox } from '@/components/InputField';
import { SelectInputBox } from "@/components/SelectInput";
import AlertMessages from '@/components/AlertMessages';
import { toUnderscoreCase } from '@/utils/format';
import clearLocalStorage from "@/utils/clearLocalStorage";

import ProductList from "../components/ProductList";
import getPromoCare from "../api/getPromoCare";
import confirmPromoCare from "../api/confirmPromoCare";
import { useOrderContext } from '@/providers/OrderProvider';

import { OrderPromoCareItem, PromoCareProduct, PromoCareOrderParams } from "../types";
import Facility from "../../shared/types/Facility";

const PromoCare: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [alertMessageOpen, setAlertMessageOpen] = useState<boolean>(false);
  const [alertMessages, setAlertMessages] = useState<string[]>([]);
  const [promoProducts, setPromoProducts] = useState<PromoCareProduct[]>([]);
  const [careProducts, setCareProducts] = useState<PromoCareProduct[]>([]);

  const [facility, setFacility] = useState<Facility>();
  const [orderPromoCareItems, setOrderPromoCareItems] = useState<OrderPromoCareItem[]>([]);
  const [note, setNote] = useState<string>('');
  const [arrivalDate, setArrivalDate] = useState<string>('');
  const [arrivalTimeZone, setArrivalTimeZone] = useState<string>('no_preference');

  const [expectedShipDate, setExpectedShipDate] = useState<string>('');
  const [shortestArrivalDate, setShortestArrivalDate] = useState<string>('');
  const [arrivalDates, setArrivalDates] = useState<string[]>([]);

  const { arrivalTimeZoneLabels } = useOrderContext();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getPromoCare();

        setFacility(response.data.facility);
        setPromoProducts(response.data.promoProducts);
        setCareProducts(response.data.careProducts);

        setExpectedShipDate(response.data.expectedShipDate);
        setShortestArrivalDate(response.data.shortestArrivalDate);
        setArrivalDates(response.data.arrivalDates);
      } catch (error) {
        console.error("Error fetching flat rate lens data:", error);
      }
    };

    fetchData();

    const fromConfirmPage = location.state?.fromConfirmPage;
    if(fromConfirmPage) {
      const storedOrderParams = localStorage.getItem('orderParams');
      if (storedOrderParams) {

        const orderParams = JSON.parse(storedOrderParams);
        const {
          note,
          expectedShipDate,
          shortestArrivalDate,
          arrivalDate,
          arrivalTimeZone,
          orderPromoCareItemsAttributes,
        } = orderParams.order;

        setNote(note);
        setExpectedShipDate(expectedShipDate);
        setShortestArrivalDate(shortestArrivalDate);
        setArrivalDate(arrivalDate);
        setArrivalTimeZone(arrivalTimeZone);
        setOrderPromoCareItems(orderPromoCareItemsAttributes);
      }

    } else {
      clearLocalStorage();
    }
  }, [location.state?.fromConfirmPage])

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const params: PromoCareOrderParams = {
      order: {
        order_type: 'promo_care',
        facilityId: facility?.id,
        note: note,
        arrivalDate: arrivalDate,
        arrivalTimeZone: arrivalTimeZone,
        expectedShipDate: expectedShipDate,
        shortestArrivalDate: shortestArrivalDate,
        orderPromoCareItemsAttributes: orderPromoCareItems
      },
    }

    try {
      const response = await confirmPromoCare(params);
      setAlertMessageOpen(false);

      localStorage.setItem("orderParams", JSON.stringify(params));
      localStorage.setItem("promoProducts", JSON.stringify(response.data.promoProducts));
      localStorage.setItem("careProducts", JSON.stringify(response.data.careProducts));

      navigate("/facility/orders/promo_care/confirm");
      window.scrollTo(0, 0);
    } catch (err: any) {
      setAlertMessageOpen(true);
      if (err.response && err.response.data && err.response.data.errors) {
        setAlertMessageOpen(true);
        setAlertMessages(err.response.data.errors.fullMessages);
        window.scrollTo(0, 0);
      } else {
        setAlertMessageOpen(false);
        console.log('An unexpected error occurred:', err);
      }
    }
  }



  return (
    <React.Fragment>
      <Layout>
        <main id="page-facility_login" className="wrapper">
          <section className="sec-contact">
            <div className="inner">
              <h1 className="c-ttl__01">ケア用品・販促品注文</h1>
              <AlertMessages open={alertMessageOpen} messages={alertMessages} />
              <div className="contact-box">
                <div className="contact-box__left">
                  <form onSubmit={handleSubmit}>
                    <div className="contact-box__form mbpc-59 js-item" id="id-01">
                      <ProductList
                        products={careProducts}
                        orderItems={orderPromoCareItems}
                        setOrderItems={setOrderPromoCareItems}
                        title="ケア用品"
                      />
                    </div>
                    <div className="contact-box__form mbpc-63 js-item" id="id-02">
                      <ProductList
                        products={promoProducts}
                        orderItems={orderPromoCareItems}
                        setOrderItems={setOrderPromoCareItems}
                        title="販促品"
                      />
                    </div>
                    <div className="contact-box__form mbpc-29 js-item" id="id-03">
                      <h2 className="c-ttl__02">配送等について</h2>
                      <InputFieldBox label="備考" value={note} onChange={e => setNote(e.target.value)} />
                      <SelectInputBox
                        label="着日指定"
                        required={true}
                        options={arrivalDates.map(date => ({ value: date, label: date }))}
                        value={arrivalDate}
                        placeholder="最短お届け日"
                        onChangeFunctions={[(newValue) => setArrivalDate(newValue || '')]}
                      />
                      <SelectInputBox
                        label="ご希望時間帯"
                        required={true}
                        options={Object.entries(arrivalTimeZoneLabels || {}).map(([key, label]) => ({
                          value: toUnderscoreCase(key),
                          label
                        }))}
                        value={arrivalTimeZone}
                        onChangeFunctions={[(newValue) => setArrivalTimeZone(newValue || "")]}
                      />
                    </div>
                    <button type="submit" className="c-btn__01">注文確認</button>
                  </form>
                </div>
                <SidebarNavigation items={[{ id: "id-01", label: "ケア用品" }, { id: "id-02", label: "販促品" }, { id: "id-03", label: "配送等について" }]} />
              </div>
            </div>
          </section>
        </main>
      </Layout>
    </React.Fragment>
  )
}

export default PromoCare;